<div class="trends-dashboard-container">
  <header class="trends-toolbar" [class.has-pending]="hasPendingChanges" aria-label="Trend controls">
    <div class="trends-toolbar__top">
      <div class="view-selector-pill" role="tablist" aria-label="Trend view">
        <div
          class="tab"
          role="tab"
          [attr.aria-selected]="activeView === 'keyMetrics'"
          [class.active]="activeView === 'keyMetrics'"
          (click)="setActiveView('keyMetrics')">
          Telephony
        </div>
        <div
          class="tab"
          role="tab"
          [attr.aria-selected]="activeView === 'insightsMetrics'"
          [class.active]="activeView === 'insightsMetrics'"
          (click)="setActiveView('insightsMetrics')">
          Insights
        </div>
      </div>

      <div class="controls-action">
        <button
          class="generate-button"
          type="button"
          data-testid="generate-trends"
          [class.has-changes]="hasPendingChanges"
          [disabled]="!canGenerate"
          (click)="generateTrends()">
          <mat-icon>{{ isAnyLoading ? 'sync' : 'auto_graph' }}</mat-icon>
          <span>Generate Trends</span>
        </button>
      </div>
    </div>

    <div class="trends-toolbar__compare">
      <div class="period-field">
        <span class="field-label">Base</span>
        <date-range-picker
          pickerContext="base"
          calendarDrops="down"
          calendarOpens="right"
          [initialStartDate]="baseStartDate"
          [initialEndDate]="baseEndDate"
          (onDateSelect)="onBaseDateRangeSelect($event)"
          [customFormat]="'DD MMM YYYY'">
        </date-range-picker>
      </div>
      <span class="vs-divider-inline">VS</span>
      <div class="period-field">
        <span class="field-label">Comparison</span>
        <date-range-picker
          pickerContext="comparison"
          calendarDrops="down"
          calendarOpens="right"
          [initialStartDate]="comparisonStartDate"
          [initialEndDate]="comparisonEndDate"
          (onDateSelect)="onComparisonDateRangeSelect($event)"
          [customFormat]="'DD MMM YYYY'">
        </date-range-picker>
      </div>
      <div class="trends-toolbar__scope">
        <filter-section buttonTitle="Filter" (filtersApplied)="onFiltersApplied()"></filter-section>
        <timezone></timezone>
      </div>
    </div>
  </header>

  <div class="trends-scroll">
  <!-- Date range validation -->
  <div class="trends-alert" *ngIf="rangeError" role="alert">
    <mat-icon>error_outline</mat-icon>
    <span>{{ rangeError }}</span>
  </div>

  <!-- Main Content Area -->
  <div class="main-content">

    <!-- Telephony: totals and averages load from two independent APIs -->
    <section class="metrics-section" *ngIf="activeView === 'keyMetrics'">
      <div class="metrics-grid">
        <article
          class="metric-card"
          *ngFor="let card of METRIC_CARDS; trackBy: trackByCard"
          [class.trend-rise]="getCardMetric(card)?.deviation_type === 'rise'"
          [class.trend-fall]="getCardMetric(card)?.deviation_type === 'fall'"
          [class.trend-flat]="getCardMetric(card)?.deviation_type === 'no_change'"
          [attr.data-testid]="'metric-card-' + card.key">
          <ng-container *ngIf="getCardState(card) as state">
            <div class="metric-card-top">
              <div class="metric-icon-wrap" aria-hidden="true">
                <mat-icon class="metric-icon">{{ card.icon }}</mat-icon>
              </div>
              <ng-container *ngIf="state.status === 'success' && getCardMetric(card) as m">
                <div
                  class="metric-change"
                  [class.increase-change]="m.deviation_type === 'rise'"
                  [class.decrease-change]="m.deviation_type === 'fall'"
                  [class.no-change]="m.deviation_type === 'no_change'">
                  <mat-icon *ngIf="m.deviation_type === 'rise'">trending_up</mat-icon>
                  <mat-icon *ngIf="m.deviation_type === 'fall'">trending_down</mat-icon>
                  {{ m.deviation_percentage | number:'1.2-2' }}%
                </div>
              </ng-container>
            </div>
            <h3 class="metric-title">{{ card.label }}</h3>

            <div class="metric-values skeleton-block" *ngIf="state.status === 'loading' || state.status === 'idle'" aria-busy="true">
              <span class="skeleton-line"></span>
              <span class="skeleton-line"></span>
            </div>

            <div class="section-error" *ngIf="state.status === 'error'" role="alert">
              <span class="section-error-text">{{ state.error }}</span>
              <button type="button" class="retry-button" (click)="retrySection(card.group)">
                <mat-icon>refresh</mat-icon> Retry
              </button>
            </div>

            <div class="metric-values" *ngIf="state.status === 'success'">
              <ng-container *ngIf="getCardMetric(card) as m; else noMetric">
                <div class="value-block">
                  <span class="value-label">Base</span>
                  <span class="value-number">
                    {{ card.isDuration ? formatDurationSeconds(m.base_value) : formatMetricValue(m.base_value) }}
                  </span>
                </div>
                <div class="value-divider" aria-hidden="true"></div>
                <div class="value-block">
                  <span class="value-label">Comparison</span>
                  <span class="value-number comparison">
                    {{ card.isDuration ? formatDurationSeconds(m.comparison_value) : formatMetricValue(m.comparison_value) }}
                  </span>
                </div>
              </ng-container>
              <ng-template #noMetric><span class="value-label">No data for the selected period</span></ng-template>
            </div>
          </ng-container>
        </article>
      </div>
    </section>

    <!-- Insights: sub-tabs, each column is its own API call -->
    <section class="insights-section" *ngIf="activeView === 'insightsMetrics'">
      <div class="insight-subtabs" role="tablist" aria-label="Insights groups">
        <button
          type="button"
          role="tab"
          class="insight-subtab"
          *ngFor="let group of INSIGHT_GROUPS; trackBy: trackByGroup"
          [class.active]="activeInsightGroup === group.key"
          [attr.aria-selected]="activeInsightGroup === group.key"
          [attr.data-testid]="'insight-subtab-' + group.key"
          (click)="setActiveInsightGroup(group.key)">
          {{ group.label }}
        </button>
      </div>

      <div class="insights-board" [class.insights-board--single]="getActiveGroup().columns.length === 1">
        <article
          class="insight-column-card"
          *ngFor="let column of getActiveGroup().columns; trackBy: trackByColumn"
          [attr.data-testid]="'insight-card-' + column">
          <h3 class="insight-column-title">{{ INSIGHTS_COLUMN_LABELS[column] || column }}</h3>

          <ng-container *ngIf="columnStates[column] as state">
            <div class="insight-skeleton skeleton-block" *ngIf="state.status === 'loading' || state.status === 'idle'" aria-busy="true">
              <span class="skeleton-line" *ngFor="let i of [1,2,3,4,5,6]"></span>
            </div>

            <div class="section-error section-error--tall" *ngIf="state.status === 'error'" role="alert">
              <mat-icon>error_outline</mat-icon>
              <span class="section-error-text">{{ state.error }}</span>
              <button type="button" class="retry-button" (click)="retrySection('column', column)">
                <mat-icon>refresh</mat-icon> Retry
              </button>
            </div>

            <div class="insight-empty-inline" *ngIf="state.status === 'success' && getInsightsRows(column).length === 0">
              <mat-icon>insights</mat-icon>
              <p>No data for the selected period and filters.</p>
            </div>

            <div class="insight-table-wrap" *ngIf="state.status === 'success' && getInsightsRows(column).length > 0">
              <table class="insight-table">
                <thead>
                  <tr>
                    <th class="col-name">Metric</th>
                    <th class="col-base">{{ getBasePeriodLabel() }}</th>
                    <th class="col-comp">{{ getComparisonPeriodLabel() }}</th>
                    <th class="col-change">Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of getInsightsRows(column); trackBy: trackByInsightRow" class="insight-row">
                    <td class="col-name">
                      <span class="metric-name" [title]="row.name">{{ row.name }}</span>
                    </td>
                    <td class="col-base">{{ formatInsightValue(row.base) }}</td>
                    <td class="col-comp">{{ formatInsightValue(row.comparison) }}</td>
                    <td class="col-change">
                      <span
                        class="deviation"
                        [class.rise]="row.change === 'rise'"
                        [class.fall]="row.change === 'fall'"
                        [class.no-change]="row.change === 'no_change'">
                        <ng-container *ngIf="row.change === 'rise'">
                          <svg class="trend-icon rise" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path fill="currentColor" d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                          </svg>
                        </ng-container>
                        <ng-container *ngIf="row.change === 'fall'">
                          <svg class="trend-icon fall" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path fill="currentColor" d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/>
                          </svg>
                        </ng-container>
                        {{ row.percentage | number:'1.2-2' }}%
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>
        </article>
      </div>
    </section>

  </div>
  </div>
</div>
